import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
} from "@angular/common/http";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<
    string,
    { response: HttpResponse<any>; timestamp: number }
  >();
  private readonly CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 jours en ms
  private readonly CACHEABLE_ENDPOINTS = [
    "/app_settings/",
    "/categories/",
    "/region/",
    "/prefectures/",
    "/communes/",
    "/company_type/",
    "/countries/",
    "/current_user/",
    "/services/",
    "/faq/",
    "/user_guide/",
    "/partenaire_type/",
    "/staff/",
    "/current_buyer_info/",
    "/limit/",
    "/article/",
    "/company/",
  ];

  constructor() {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    console.log("Intercepting request to:", request.url); // Debug 1

    // Vider le cache du current_user si c'est une requête de déconnexion
    if (request.method === "POST" && request.url.includes("/logout")) {
      this.clearUserSpecificCache();
    }

    if (request.method !== "GET") {
      console.log("Not caching - not a GET request"); // Debug 2
      return next.handle(request);
    }

    const shouldCache = this.CACHEABLE_ENDPOINTS.some((endpoint) => {
      const matches = request.url.includes(endpoint);
      console.log(`Checking ${endpoint}: ${matches}`); // Debug 3
      return matches;
    });

    if (!shouldCache) {
      console.log("Not caching - endpoint not in cacheable list"); // Debug 4
      return next.handle(request);
    }

    const cachedEntry = this.cache.get(request.urlWithParams);
    const now = Date.now();

    if (cachedEntry && now - cachedEntry.timestamp < this.CACHE_DURATION) {
      return of(cachedEntry.response.clone());
    }

    console.log("Should cache this request"); // Debug 5

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && shouldCache) {
          this.cache.set(request.urlWithParams, {
            response: event.clone(),
            timestamp: now,
          });
        }
      })
    );
  }

  // Vider le cache lié à l'utilisateur
  clearUserSpecificCache(): void {
    const keysToDelete: string[] = [];

    this.cache.forEach((value, key) => {
      if (
        key.includes("/current_user/") ||
        key.includes("/current_buyer_info/") ||
        key.includes("/company/")
      ) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.cache.delete(key));
    console.log("User-specific cache cleared");
  }

  // Vider tout le cache
  clearAllCache(): void {
    this.cache.clear();
    console.log("Complete cache cleared");
  }

  // Méthode pour invalider manuellement le cache
  invalidateCache(urlPattern: string): void {
    Array.from(this.cache.keys())
      .filter((key) => key.includes(urlPattern))
      .forEach((key) => this.cache.delete(key));
  }
}
